import { AxiosError } from "axios";
import Vue from "vue";
import * as Sentry from "@sentry/browser";
import { Vue as VueIntegration } from "@sentry/integrations";
import { Integrations } from "@sentry/tracing";
import store from "@/store";
import { HttpStatusCode } from "@/api/types";
import { RpcError, StatusCode } from "grpc-web";

export const setSentry = (): void => {
  const env = process.env["NODE_ENV"];
  if (env === "development") return;

  Sentry.init({
    dsn:
      "https://612ff499541643bda222cc991cdd9f94@o405964.ingest.sentry.io/5419380",
    environment: env,
    integrations: [
      new VueIntegration({
        Vue,
        tracing: true
      }),
      new Integrations.BrowserTracing()
    ],
    // NOTE: トレースのサンプリング率を 0 に設定することで、トレースの送信を無効にします。
    //       2024/03/07に、Sentryにおけるトレースのサンプリング率を0にするという意思決定が行われました。
    //       (背景としては、コスト削減と有効活用できていないケースが多いため)
    //       Sentryのトレースは無効にし、必要に応じてDataDog APM等でトレースを行う方法を検討してください。
    tracesSampleRate: 0,
    // NOTE: もし、エラーサンプルレートを下げたい場合は、以下の設定を追加する
    // ref: https://docs.sentry.io/platforms/node/configuration/sampling/
    // sampleRate: 0.5,
    beforeSend(event, hint) {
      if (isSkipApiError(hint)) {
        return null;
      }

      if (isSkipGrpcError(hint)) {
        return null;
      }

      return event;
    },
    ignoreErrors: ["Network Error"],
    blacklistUrls: [
      // NOTE: Chrome拡張機能のエラーは除外する
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /extensions\//i
    ]
  });
};

const isSkipApiError = (hint: Sentry.EventHint | undefined) => {
  const isApiError =
    hint?.originalException instanceof Error &&
    !!(hint?.originalException as AxiosError)?.response;

  if (isApiError) {
    const status = (hint?.originalException as AxiosError)?.response?.status;

    // NOTE: 一部のエラー報告は除外する
    switch (status) {
      case HttpStatusCode.Unauthorized:
        return true;
      case HttpStatusCode.Forbidden:
        return true;
      case HttpStatusCode.NotFound:
        return true;
      case HttpStatusCode.UnprocessableEntity:
        return true;
      default:
        return false;
    }
  }

  return false;
};

const isSkipGrpcError = (hint: Sentry.EventHint | undefined) => {
  const isGrpcError =
    hint?.originalException instanceof Error &&
    !!(hint?.originalException as RpcError)?.code &&
    !!(hint?.originalException as RpcError)?.message;

  if (!isGrpcError) return false;

  const rpcError = hint?.originalException as RpcError;

  const code = rpcError.code;
  const skipCodeList = [
    StatusCode.NOT_FOUND,
    StatusCode.UNAUTHENTICATED,
    StatusCode.DEADLINE_EXCEEDED
  ];
  if (skipCodeList.includes(code)) return true;

  const message = rpcError.message;
  const skipMessageList = ["not be able to send scout message"];
  if (skipMessageList.includes(message)) return true;

  // 上記に当てはまらない場合、エラーを通知する
  return false;
};

export const setSentryUser = (): void => {
  const partnerUser = store.state.domains.user.partnerUser;
  if (partnerUser) {
    Sentry.setUser({
      "Partner User ID": partnerUser.id,
      "Partner ID": partnerUser.partner.id,
      "Partner Company ID": partnerUser.partner.company.id,
      "Partner Company Name": partnerUser.partner.company.name
    });
  }
};

export const removeSentryUser = (): void => {
  Sentry.configureScope(scope => scope.setUser(null));
};
